/**
 * The two calls B0 makes, typed from the generated contract.
 *
 * Nothing here hand-declares a payload — the shapes come from `api/generated/openapi.ts`,
 * which `npm run gen-client:check` diffs against `openapi.json` on every build
 * (MCK-20260521-002).
 *
 * `POST /workspace/claim` **does not create a workspace**. It fills in the one the caller has
 * had since they walked through either door, and the row's id is unchanged before and after
 * (`workspaces/service.py:claim_workspace`). That is why this file has no "create" verb in
 * it and must not grow one.
 */

import { api, unwrap, unwrapWithETag } from '@/api/client';
import type { Representation } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type Workspace = Schemas['WorkspaceResponse'];
export type WorkspaceClaim = Schemas['WorkspaceClaimRequest'];

/** The caller's own company. B0 reads it to prefill; it is readable by any member. */
export function readWorkspace(): Promise<Workspace> {
  return unwrap(api.GET('/api/v1/workspace'));
}

/**
 * The same read, keeping the `ETag` the endpoint sends.
 *
 * **This is what the cached query runs**, because the validator is a property of *the
 * representation the client is holding* — a ref beside the query would drift the moment a
 * refetch landed, and `PATCH /workspace` refuses outright without one
 * (`KLQ-20260819-008` §1). B0 itself never sends an `If-Match`: `POST /workspace/claim` is
 * guarded by the state it consumes, and the `409` it answers names the real conflict rather
 * than "your copy is stale".
 *
 * It stays in this module with the rest of the workspace resource. B3 writes the row and B0
 * created it, but two query keys for one resource are two answers to one question — the same
 * call B2 made when the member list moved to whoever owns it.
 */
export function readWorkspaceRepresentation(): Promise<Representation<Workspace>> {
  return unwrapWithETag(api.GET('/api/v1/workspace'));
}

/**
 * B0's single write, and the only endpoint in the system that sets `claimed_at`.
 *
 * No `Idempotency-Key` and none needed: the endpoint is guarded by the state it consumes, so
 * a replay is a `409` rather than a second workspace (`workspaces/router.py:claim_workspace`).
 * The double-submit guard on the button is about the user's patience, not about correctness.
 */
export function claimWorkspace(body: WorkspaceClaim): Promise<Workspace> {
  return unwrap(api.POST('/api/v1/workspace/claim', { body }));
}

// --- The norm catalogue (`KLQ-20260827-001`) ----------------------------------
//
// **Reference data, and the one read in this app that is genuinely identical for every
// tenant.** Five norms and about thirty phase rows, changing when a law does. The endpoint
// answers `Cache-Control: private, max-age=3600` and its docstring says the client is
// expected to hold it for the session rather than ask again per screen; `queries.ts` is
// where that promise is actually kept.
//
// It lives in `features/setup` for the same reason the workspace resource does: B0 is the
// screen that first needs it, every later reader composes the one query rather than opening a
// second one, and a second key for one answer is two answers to one question. The studio
// settings surface (`features/norms`) reads it from here.

export type Norm = Schemas['NormResponse'];
export type NormVersion = Schemas['NormVersionResponse'];
export type NormPhase = Schemas['NormPhaseResponse'];

/**
 * Every norm, every edition, every rung.
 *
 * Unfiltered and unpaginated by design — the whole catalogue is smaller than one page of
 * anything else in this API, and a screen that asked for a slice of it would spend a round
 * trip to save nothing.
 */
export function readNormCatalogue(): Promise<Norm[]> {
  return unwrap(api.GET('/api/v1/norms'));
}
